import React from "react";
import ReactDOM from "react-dom";
import OverActions from "data/OverActions";
import OverModal from "lib/over/OverModal";
import userStore from "data/userStore";
import Services from "lib/Services";
import AboutStore from "data/meta/AboutStore";
import compareVersions from "compare-versions";
import StereotypesStore from "data/StereotypesStore";
import Dispatcher from "../Dispatcher";
import query_string from "../query_string";
import promisify from "../lib/promisify";
import { appAlert } from "../lib/withOver";
import SearchFormActions from "../data/SearchFormActions";

const DEEP_LINK_STORAGE_KEY = "ao:deep-link";
const RE_LINK_PARAM = /[?&]link=[a-zA-Z0-9]+(&|$)/;
function cleanUpDeepLink() {
    const loc = window.location;
    if (loc.search) {
        const next = loc.href.replace(RE_LINK_PARAM, "$1");
        if (next !== loc.href) {
            window.history.replaceState({}, "", next);
        }
    }
    window.sessionStorage.removeItem(DEEP_LINK_STORAGE_KEY);
}

/**
 * I handle incoming deep-links, potentially aborting initialization.
 * @param orgKey The active org's key
 * @param fluxContext The current Flux context
 * @return {boolean} Whether to abort initialization
 */
function handleDeepLink(orgKey, fluxContext) {
    const deepLinkJSON = window.sessionStorage.getItem(DEEP_LINK_STORAGE_KEY);
    if (!deepLinkJSON) return;
    const oops = (msg, ...moar) =>
        console.warn(`DEEPLINK: ${msg}`, ...moar);
    let deepLinkState = {};
    try {
        deepLinkState = JSON.parse(deepLinkJSON);
    } catch (e) {
        oops("Failed to parse:", deepLinkJSON);
        cleanUpDeepLink();
        return;
    }
    const qs = query_string.parse();
    if (qs.link !== deepLinkState.link) {
        oops("Expired link key");
        cleanUpDeepLink();
        return;
    }
    const data = query_string.parse(
        deepLinkState.search,
        (agg, it) =>
            agg instanceof Array
                ? agg.concat(it)
                : [ agg, it ],
    );
    if (data.go === "de-search-result") {
        if (!data.org) {
            oops("No org specified");
        } else if (!data.stereo) {
            oops("No stereo specified");
        } else if (!data.f) {
            oops("No field(s) specified");
        } else if (userStore.getAllOrgs()
            .every(it => it.key !== data.org)) {
            oops("Unknown org specified");
        } else if (data.org !== orgKey) {
            promisify(Services.saveActiveOrgs([ data.org ]))
                .then(() => window.location.reload())
                .catch(jqXHR => {
                    if (jqXHR.status < 500) return;
                    appAlert("Failed to switch organizations");
                });
            return true; // skip the rest of bootstrapping
        } else {
            // we are go!
            window.location.hash = "#de";
            if (typeof data.f === "string") {
                data.f = [ data.f ];
            }
            Dispatcher.contextDispatch(fluxContext, {
                type: SearchFormActions.DEEP_LINKED_SEARCH,
                key: data.stereo,
                fieldData: data.f.map(it => {
                    const parts = it.split(":");
                    return [ parts.shift(), parts.join(":") ];
                }),
            });
        }
    }
    cleanUpDeepLink();
}

class MainController extends Backbone.Controller {

    constructor(...args) {
        super(...args);
        this.initialize = this.initialize.bind(this);
        this.onNavigate = this.onNavigate.bind(this);
    }

    initialize(options) {
        this.model = options.model;
        this.app = options.app;
    }

    onNavigate(org, key, id, ekey, eid, shortname, trigger) {
        if (trigger == null) { trigger = false; }
        this.dispatch("user:activity");
        // Hybrid is only ever viewing data through the lens of a single org.
        // HJC-250 - For now if org is not passed in we will set it to the
        // default.
        if(!org) {
            org = userStore.getActiveOrgKey();
        }

        // This now updates the url but triggers loading based on trigger
        // DQR passes true but DE wont pass it and uses the default
        // the url so that we can load data into the appropriate
        //
        if (ekey) {
            this.app.router.navigate(["de", org, key, id, ekey, eid, shortname].join("~"), {trigger});
        } else {
            this.app.router.navigate(["de", org, key, id].join("~"), {trigger});
        }
    }

    loadInitialData() {
        userStore.getUserLO().promise().then(() => {
            const orgKey = userStore.getActiveOrgKey();
            const fluxContext = this.model.get("fluxContext");
            if (handleDeepLink(orgKey, fluxContext)) return;
            this.dispatch("monitoring:set:username", userStore.getUsername());
            this.dispatch("monitoring:set:active:organization", orgKey);

            //console.log('user', user)
            const dg = [];
            dg.push(
                Services.loadArmusScript(),
            );

            dg.push(
                AboutStore.getApiVersionLO().promise()
                    .then(data => {
                        this.dispatch(
                            "monitoring:add:release:version",
                            "client",
                            AboutStore.getClientVersion(),
                        );
                        this.dispatch(
                            "monitoring:add:release:version",
                            "application",
                            data.application,
                        );

                        _.forEach(
                            data.meta,
                            meta => this.dispatch(
                                "monitoring:add:release:version",
                                `meta:${meta.key}`,
                                meta.version,
                            ),
                        );

                        let actual = AboutStore.getApiVersion()
                            .application
                            .split(":")[2];
                        // the "inter-project" part of version numbers should be ignored
                        actual = actual.replace(/-[A-Z]{2,}-[0-9]+-/, "-");
                        // the build part of version numbers should be ignored
                        actual = actual.replace(/_b(\d+)/, "+$1");
                        const required = AboutStore.getRequiredApiVersion();
                        if (compareVersions(actual, required) < 0) {
                            const message = `API version '${actual}' is too old; at least '${required}' is required.`;
                            $(".loading-modal")
                                .find(".loading-body p")
                                .text(message)
                                .end()
                                .find("img")
                                .hide();
                            // this emulates the IT framework's result
                            $("body")
                                .addClass("IT-error")
                                .addClass("IT-complete")
                                .attr("data-error", message);
                            throw new Error(message);
                        }
                    }),
            );
            dg.push(
                StereotypesStore.getAllStereotypesLO().promise()
                    .then(data => {
                        const types = this.model.get("stereotypes");
                        types.default = data.defaultKey;
                        types.populate(
                            data.stereotypes,
                            it => it.magicRoot || (it.magicRootKey != null),
                        );

                        const lookups = this.model.get("lookups");
                        lookups.populate(
                            data.stereotypes,
                            it => !it.magicRoot && (it.magicRootKey == null) && it.creatables.length,
                        );

                        const dataQualityReport = this.model.get(
                            "dataQualityReport");
                        const dqrTypes = dataQualityReport.get("stereotypes");
                        dqrTypes.populate(
                            data.stereotypes,
                            it => it.magicRootKey != null,
                        );
                        dataQualityReport.set("stereotype", dqrTypes.first());
                        return data; // I'm a real boy!
                    }),
            );
            Promise.all(dg).then(() => {
                this.app.layout.render();
                this.createOverContainer();
                Backbone.history.start();
                $("body").addClass("loaded");
            });
        });
    }

    createOverContainer() {
        const handleHide = key =>
            Dispatcher.dispatch({
                type: OverActions.HIDE,
                key,
            });
        ReactDOM.render(
            <OverModal onHide={handleHide}/>,
            document.getElementById("loading-modal-container")
        );
    }

}

MainController.prototype.events = {
    "load:initialData": "loadInitialData",
    "navigate": "onNavigate",
};

MainController.prototype.cache = require("lib/CacheManager");

export default MainController;



// WEBPACK FOOTER //
// ./app/controllers/MainController.js